import React from 'react';
import { Row, Col, Button } from 'antd';
import { PhoneOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import styles from './styles.module.css';
import { useParams, useNavigate } from 'react-router-dom';

interface PracticeData {
  title: string;
  description: string;
  services?: string[];
}

interface PracticeDataType {
  [key: string]: PracticeData;
}

const practiceData: PracticeDataType = {
  'corporate-transactions': {
    title: 'Corporate & Commercial Transactions',
    description:
      'We provide comprehensive legal support for the commercial activities that drive your business. From day-to-day agreements to major strategic deals, we help clients structure transactions that are clear, enforceable, and aligned with their business goals.',
    services: [
      'Commercial Agreements — Drafting, reviewing, and negotiating contracts essential to daily operations',
      'Strategic Business Transactions — Joint ventures, partnerships, asset purchases, revenue-sharing agreements',
      'Risk Allocation & Contract Strategy — Advising on indemnity structures, dispute mechanisms, performance obligations',
    ],
  },
  'mergers-acquisitions': {
    title: 'Mergers & Acquisitions',
    description:
      'We represent buyers and sellers in business acquisitions, divestitures, internal reorganizations, and equity transactions. Our M&A practice focuses on delivering practical, strategic guidance throughout every stage of a deal—from early structuring to closing.',
  },
  'business-formation': {
    title: 'Business Formation & Governance',
    description:
      'We help entrepreneurs and established businesses create the right legal foundation for growth. From selecting an appropriate entity structure to drafting governance documents, we ensure clients begin and operate their companies with clarity, compliance, and long-term strategy in mind.',
  },
  contracts: {
    title: 'Contracts & Negotiations',
    description:
      'Contracts shape the relationships that define your business. We draft, review, and negotiate the agreements that govern operations, protect assets, and create reliable commercial frameworks.',
  },
  'cross-border': {
    title: 'Cross-Border & International Business',
    description:
      'We help companies navigate the complexities of operating across multiple jurisdictions. From structuring international transactions to advising on regulatory risks, we provide strategic support for businesses with global reach.',
  },
  investments: {
    title: 'Private Investments & Financing',
    description:
      'We guide investors, lenders, and businesses through private financing transactions. Our work includes structuring investments, negotiating loan terms, preparing credit agreements, and managing collateralized transactions.',
  },
  compliance: {
    title: 'Regulatory & Compliance Support',
    description:
      'We help clients comply with business regulations at the federal, state, and international level. Our approach focuses on practical, business-friendly compliance solutions that support growth without creating unnecessary burdens.',
  },
};

function PracticeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const practice =
    slug
      ? practiceData[slug] ?? practiceData['corporate-transactions']
      : practiceData['corporate-transactions'];

  return (
    <div className={styles.practiceContainer}>
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            className={styles.backButton}
          >
            Back to Practices
          </Button>
          <h1 className={styles.heroTitle}>{practice.title}</h1>
          <p className={styles.heroSubtitle}>{practice.description}</p>
        </div>
      </div>

      <div className={styles.contentSection}>
        <Row gutter={[60, 40]}>
          <Col xs={24} lg={16}>
            <div className={styles.mainContent}>
              <h2>Overview</h2>
              <p>{practice.description}</p>

              {practice.services && (
                <>
                  <h2>Core Services</h2>
                  <ul className={styles.servicesList}>
                    {practice.services.map((service: string, index: number) => (
                      <li key={index}>{service}</li>
                    ))}
                  </ul>
                </>
              )}

              <div className={styles.whySection}>
                <h2>Why This Matters</h2>
                <p>
                  Well-structured legal agreements are the foundation of every
                  successful business. We help clients understand their rights and
                  obligations, avoid costly disputes, and protect revenue and
                  relationships.
                </p>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={8}>
            <div className={styles.sidebar}>
              <div className={styles.ctaCard}>
                <h3>Ready to Discuss Your Needs?</h3>
                <p>Speak with us about your next transaction</p>
                <Button
                  type="primary"
                  block
                  size="large"
                  icon={<PhoneOutlined />}
                  className={styles.ctaButton}
                  onClick={() => navigate('/contact')}
                >
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default PracticeDetail;
